import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

import { SET_MY_LANGUAGE } from '../graphql/mutations';
import { resolveInitialLanguage, storeLanguage } from './detect';
import {
  getActiveLanguage,
  getLocaleTag,
  setActiveLanguage,
  translate,
  translatePlural,
  TranslateVars,
} from './translate';
import { Language, isSupportedLanguage } from './types';

interface I18nValue {
  /** Language the app is painted in right now. */
  language: Language;
  /**
   * True while the current screen forces its own language (Asset Control is
   * Spanish-only for PROCOMER). `setLanguage` still records the preference,
   * but the selector is locked so the choice does not look ignored.
   */
  locked: boolean;
  /** BCP-47 tag for Intl formatting in the current language. */
  locale: string;
  /** Change the language and persist it to the account. */
  setLanguage: (language: Language) => void;
  /** True while the account write is in flight. */
  saving: boolean;
  /** Set when the account write failed; the UI still switched locally. */
  saveError: boolean;
  t: (key: string, vars?: TranslateVars, fallback?: string) => string;
  tPlural: (baseKey: string, count: number, vars?: TranslateVars) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

interface Props {
  /**
   * `language` column of the signed-in account: a language code once chosen,
   * `null` when the person has never picked one, `undefined` while the
   * account row is still loading.
   */
  accountLanguage?: string | null;
  /** True once the account row has actually been fetched. */
  accountLoaded?: boolean;
  /**
   * Language the current screen must be shown in, overriding the person's
   * preference without changing it. See `forcedLanguageFor`.
   */
  forcedLanguage?: Language;
  children: React.ReactNode;
}

/**
 * Language resolution, in order of authority:
 *
 *   1. the account (`user.language`) — follows the person to any device;
 *   2. the last choice made on this device (localStorage) — avoids a flash of
 *      the wrong language on reload, before the account row arrives;
 *   3. the browser / OS language;
 *   4. Spanish.
 *
 * When the account has never had a language (1 is empty), whatever 2 or 3
 * resolved to is written up to the account once, so the machine's language
 * becomes the saved preference on first login instead of being re-detected
 * forever.
 */
export const I18nProvider: React.FC<Props> = ({
  accountLanguage,
  accountLoaded,
  forcedLanguage,
  children,
}) => {
  // `language` is the person's preference; `effective` is what is on screen.
  const [language, setLanguageState] = useState<Language>(resolveInitialLanguage);
  const effective = forcedLanguage ?? language;
  const [saveError, setSaveError] = useState(false);
  // The first-login backfill must happen once per session, not on every
  // re-render of the account row.
  const hasBackfilled = useRef(false);
  // Set as soon as the person picks a language here. `accountLanguage` is a
  // snapshot taken when the account row was fetched, so without this the
  // effect below would read the pre-switch value and undo the choice.
  const hasChosen = useRef(false);

  const [setMyLanguage, { loading: saving }] = useMutation(SET_MY_LANGUAGE);

  // Applied during render, not in an effect: plain helpers such as
  // `getLabel()` read the module-level language synchronously while this same
  // pass renders the children below.
  if (getActiveLanguage() !== effective) {
    setActiveLanguage(effective);
  }

  const persist = useCallback(
    (next: Language) => {
      setSaveError(false);
      setMyLanguage({ variables: { language: next } }).catch(() => {
        // Non-fatal: the switch already applied locally and is in
        // localStorage, so only the cross-device sync is lost.
        setSaveError(true);
      });
    },
    [setMyLanguage],
  );

  const setLanguage = useCallback(
    (next: Language) => {
      if (!isSupportedLanguage(next)) return;
      if (!forcedLanguage) setActiveLanguage(next);
      setLanguageState(next);
      storeLanguage(next);
      // A deliberate choice is always worth saving, even if it matches what
      // the account already has (it may have been the detected default).
      hasBackfilled.current = true;
      hasChosen.current = true;
      persist(next);
    },
    [persist, forcedLanguage],
  );

  useEffect(() => {
    if (!accountLoaded) return;
    // An explicit choice made in this session outranks the account snapshot,
    // which may still hold the value from before the switch.
    if (hasChosen.current) return;

    if (isSupportedLanguage(accountLanguage)) {
      // The account wins over whatever this device had cached.
      if (accountLanguage !== language) {
        if (!forcedLanguage) setActiveLanguage(accountLanguage);
        setLanguageState(accountLanguage);
      }
      storeLanguage(accountLanguage);
      hasBackfilled.current = true;
      return;
    }

    // No preference on the account yet: adopt what we detected.
    if (!hasBackfilled.current) {
      hasBackfilled.current = true;
      storeLanguage(language);
      persist(language);
    }
  }, [accountLoaded, accountLanguage, language, persist, forcedLanguage]);

  // Keeps screen readers and the browser's own UI (spellcheck, translation
  // prompts) in step with what is on screen.
  useEffect(() => {
    document.documentElement.lang = effective;
    // dayjs's locale is global and was being set to 'es' at module scope by
    // whichever chunk happened to load first, so a month name came out in
    // whatever language the last-loaded page had asked for. Owning it here
    // makes it follow the person's choice instead of their navigation.
    dayjs.locale(effective);
  }, [effective]);

  const value = useMemo<I18nValue>(
    () => ({
      language: effective,
      locked: Boolean(forcedLanguage),
      locale: getLocaleTag(effective),
      setLanguage,
      saving,
      saveError,
      t: translate,
      tPlural: translatePlural,
    }),
    [effective, forcedLanguage, setLanguage, saving, saveError],
  );

  return (
    <I18nContext.Provider value={value}>
      {/*
        Remounted on every change of the on-screen language, including
        entering or leaving a Spanish-only screen. Plain helpers such as `getLabel()`
        read the language from module scope instead of from this context, so a
        subtree that does not consume the context would otherwise keep
        rendering the previous language until something else re-rendered it.
        Switching is a rare, deliberate action, so a remount here buys
        correctness everywhere without a per-component subscription.
        App-level state (the signed-in user, the selected date) lives above
        this provider and survives the swap.
      */}
      <React.Fragment key={effective}>{children}</React.Fragment>
    </I18nContext.Provider>
  );
};

/**
 * Read-only view used when a component renders outside the provider — unit
 * tests and isolated renders. Translation still works, because the active
 * language lives at module scope; only switching (which needs the account
 * mutation and a re-render) is unavailable. Degrading here rather than
 * throwing keeps a missing provider from white-screening the app.
 */
const standaloneValue = (): I18nValue => ({
  language: getActiveLanguage(),
  locked: false,
  locale: getLocaleTag(),
  setLanguage: setActiveLanguage,
  saving: false,
  saveError: false,
  t: translate,
  tPlural: translatePlural,
});

export const useI18n = (): I18nValue =>
  useContext(I18nContext) ?? standaloneValue();

/** Shorthand for components that only need the translate function. */
export const useT = (): I18nValue['t'] => useI18n().t;
